import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Button,
  Grid,
  Autocomplete,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { dspState } from '../../../atoms/DspState';
import { toggleEditWarehouse } from '../../../atoms/ToggleEditWarehouse';
import { warehouseState } from '../../../atoms/WarehouseState';
import { AxlButton } from '../../../components/AxlButton';
import { addWarehouse, getGeocode, listClient } from '../../../repositories/HttpRepository';
import * as Constants from '../constants';
import InputField from '../../../components/InputField/input';
import Select from '../../../components/SelectField/select';
import GeocodeResult from './GeocodeResult';
import * as Styles from './styles';

const AddWarehouse = (props) => {
  const [errorAdd, setErrorAdd] = useState('');
  const [typeOptions, setTypeOptions] = useState([]);
  const [warehouse, setWarehouse] = useState({
    alias: null,
    owner_id: null,
    owner_type: null,
    notes: null,
    address: {
      street: null,
      street2: null,
      city: null,
      state: null,
      zipcode: null,
      lat: null,
      lng: null,
      full: null,
    },
  });
  const [enableAddBtn, setEnableAddBtn] = useState(false);
  const [changeGeocode, setChangeGeocode] = useState(false);
  const [disableLatLng, setDisableLatLng] = useState(true);
  const setOnEditWarehouse = useSetRecoilState(toggleEditWarehouse);
  const [displayGeocodeResult, setDisplayGeocodeResult] = useState(false);
  const [geoCodeStatus, setGeoCodeStatus] = useState(false);
  const [geoCodeInfos, setGeoCodeInfos] = useState({});
  const [selectedWarehouseType, setSelectedWarehouseType] = useState([]);
  const [warehouses, setWarehouses] = useRecoilState(warehouseState);
  const [dspList, setDspList] = useRecoilState(dspState);

  const navigate = useNavigate();
  const { classes } = Styles.useStyles();

  // // Check form can submit or not
  const checkSubmit = () => {
    return (
      warehouse.alias &&
      warehouse.owner_type &&
      warehouse.address.street &&
      warehouse.address.city &&
      warehouse.address.state &&
      warehouse.address.zipcode &&
      warehouse.address.lat &&
      warehouse.address.lng
    );
  };

  useEffect(() => {
    if (checkSubmit()) {
      setEnableAddBtn(true);
    } else {
      setEnableAddBtn(false);
    }
  }, [warehouse]);

  function submitForm(e) {
    e.preventDefault();

    if (changeGeocode) {
      toast.error('You just change your address, pls geocode it before the update');
      return;
    }

    if (!enableAddBtn) {
      toast.error('Please fill all require fields!');
      return;
    }

    addWarehouse(warehouse).then((res) => {
      if (res.ok) {
        toast.success('added warehouse !');
        finishAdd();
      } else {
        setErrorAdd('Server is having problem, please try again later.');
      }
    });
  }

  function finishAdd() {
    setWarehouses({
      ...warehouses,
      screen: 'added ware house',
    });

    navigate('/warehouses');
  }

  const dspOptions = useMemo(
    () =>
      dspList?.items?.map((dsp) => ({
        title: dsp.code ?? dsp.company,
        value: dsp.id,
      })),
    [dspList],
  );

  function handeChangeWarehouseType(e) {
    // set owner types
    // no-dd-sa:javascript-code-style/assignment-name
    let warehouse_type_selected = e.target.value;
    setSelectedWarehouseType(warehouse_type_selected);
    if (warehouse_type_selected === 1) {
      // our WH
      setTypeOptions(Constants.defaultTypeOptions());
    } else if (warehouse_type_selected === 2) {
      listClient({ size: 1000000 }).then((resp) => {
        if (resp.ok) {
          let items = resp.data.items;
          let newTypes = [];
          let i = 0;
          for (i = 0; i < items.length; i++) {
            newTypes[i + 1] = { value: items[i].id, title: items[i].name };
          }
          setTypeOptions(newTypes);
        }
      });
    } else if (warehouse_type_selected === 3) {
      setTypeOptions(dspOptions);
    }
  }

  function handleOwnerTypeChange(value) {
    if (selectedWarehouseType === Constants.OWNER_TYPE_JITSU) {
      setWarehouse({
        ...warehouse,
        owner_id: null,
        owner_type: value, // Main or Mobile Hub
      });
    } else if (selectedWarehouseType === Constants.OWNER_TYPE_CLIENT) {
      setWarehouse({
        ...warehouse,
        owner_id: value,
        owner_type: Constants.OWNER_TYPE_CLIENT_STR,
      });
    } else if (selectedWarehouseType === Constants.OWNER_TYPE_COURIER) {
      setWarehouse({
        ...warehouse,
        owner_id: value,
        owner_type: Constants.OWNER_TYPE_COURIER_STR,
      });
    }
  }

  function handleCancel(e) {
    navigate('/warehouses');
  }

  function handleOnClickGeocode() {
    // Set latitude & longitude
    const { address } = warehouse;

    if (address.street && address.city && address.state && address.zipcode) {
      getGeocode(warehouse.address).then((resp) => {
        let lat = 0;
        let lng = 0;
        if (resp.ok && resp.data.latitude && resp.data.longitude) {
          lat = resp.data.latitude;
          lng = resp.data.longitude;
          setDisableLatLng(true);
          setGeoCodeStatus(true);
        } else {
          setDisableLatLng(false);
          setGeoCodeStatus(false);
        }
        setChangeGeocode(false);
        setWarehouse({
          ...warehouse,
          address: {
            ...warehouse.address,
            lat: lat,
            lng: lng,
          },
        });
        setGeoCodeInfos(warehouse);
        setDisplayGeocodeResult(true);
      });
    } else {
      toast.error('Please fill all the addresses information!');
    }
  }

  function handleOnChangeInput(e) {
    const key = e.target.getAttribute('id').trim();
    const value = e.target.value;

    let tmp = { ...warehouse };
    tmp[key] = value;
    setWarehouse(tmp);
  }

  function validateLatLng(data) {
    const lat = isNaN(parseFloat(data.lat)) ? 0 : parseFloat(data.lat);
    const lng = isNaN(parseFloat(data.lng)) ? 0 : parseFloat(data.lng);
    return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
  }

  function handleOnChangeInputAddress(e) {
    const key = e.target.getAttribute('id').trim();
    let value = e.target.value;

    if (key !== 'lat' && key !== 'lng') {
      setChangeGeocode(true);
    }
    let tmp = { ...warehouse.address };
    tmp[key] = value;
    if (key === 'lat' || key === 'lng') {
      if (!validateLatLng(tmp)) {
        toast.error('Invalid latitude or longitude');
        return;
      }
    }
    setWarehouse({ ...warehouse, address: tmp });

    if (geoCodeStatus === false && tmp.lat && tmp.lng) {
      setDisplayGeocodeResult(false);
    }
  }

  function joinClassnames(...classnames) {
    return classnames.filter((i) => i).join(' ');
  }

  return (
    <Dialog
      open={true}
      onClose={() => {
        navigate('/warehouses');
      }}
      aria-labelledby="form-dialog-title"
      maxWidth="md"
    >
      <DialogTitle id="form-dialog-title" className={classes.Dialog}>
        <Typography variant="h4">
          Add New Warehouse
          <CloseIcon className={classes.closeBtn} onClick={handleCancel} />
        </Typography>
      </DialogTitle>
      <DialogContent className={classes.formAdd}>
        <Typography color="error" variant="caption">
          {errorAdd}
        </Typography>
        <form className={classes.root} noValidate autoComplete="off">
          <Grid container spacing={3}>
            <InputField label="Warehouse Alias" column="12" id="alias" onChange={handleOnChangeInput}></InputField>
            <Select
              label="Warehouse Type"
              column="6"
              id="warehouse-type"
              options={Constants.defaultWarehouseTypeOptions}
              onChange={handeChangeWarehouseType}
            ></Select>
            <Grid size={6}>
              <Autocomplete
                options={typeOptions}
                renderInput={(params) => (
                  <>
                    <span>Owner Type</span>
                    <TextField {...params} variant="outlined" size="small" fullWidth />
                  </>
                )}
                getOptionLabel={(option) => `[${option.value}] - ${option.title}`}
                onChange={(e, values) => handleOwnerTypeChange(values?.value)}
              />
            </Grid>
            <InputField
              label="Address Line 1"
              column="12"
              id="street"
              onChange={handleOnChangeInputAddress}
            ></InputField>
            <InputField
              label="Address Line 2"
              label2="(optional)"
              column="12"
              id="street2"
              onChange={handleOnChangeInputAddress}
            ></InputField>
            <InputField label="City" column="4" id="city" onChange={handleOnChangeInputAddress}></InputField>
            <InputField label="State" column="4" id="state" onChange={handleOnChangeInputAddress}></InputField>
            <InputField label="Zipcode" column="4" id="zipcode" onChange={handleOnChangeInputAddress}></InputField>
            <InputField
              label="Latitude"
              column="5"
              id="lat"
              disabled={disableLatLng}
              value={warehouse.address.lat}
              onChange={handleOnChangeInputAddress}
            ></InputField>
            <InputField
              label="Longitude"
              column="5"
              id="lng"
              disabled={disableLatLng}
              value={warehouse.address.lng}
              onChange={handleOnChangeInputAddress}
            ></InputField>
            <Grid size={2}>
              <Button variant="contained" className={classes.GeoCode} onClick={handleOnClickGeocode}>
                GEOCODE
              </Button>
            </Grid>
            <Grid size={12}>
              {displayGeocodeResult && <GeocodeResult data={geoCodeInfos} status={geoCodeStatus} />}
            </Grid>
            <InputField label="Prefix" column="12" id="prefix" onChange={handleOnChangeInput}></InputField>
            <InputField label="Access code" column="12" id="access_code" onChange={handleOnChangeInput}></InputField>
            <Grid size={12}>
              <span>Pickup Instructions</span>
              <TextField
                id="pickup_notes"
                variant="outlined"
                fullWidth
                size="small"
                placeholder="Add pickup note/instructions here…"
                onChange={handleOnChangeInput}
                multiline
                rows={3}
              />
            </Grid>
            <Grid size={12}>
              <span>Return Instructions</span>
              <TextField
                id="return_note"
                variant="outlined"
                fullWidth
                size="small"
                placeholder="Add return note/instructions here…"
                onChange={handleOnChangeInput}
                multiline
                rows={3}
                slotProps={{
                  htmlInput: { maxLength: 1024 },
                }}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions className={classes.dialogAction}>
        <AxlButton onClick={handleCancel} isCancel variant="contained" className={classes.cancelBtn}>
          CANCEL
        </AxlButton>
        <AxlButton variant="contained" onClick={submitForm}>
          ADD
        </AxlButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddWarehouse;
